/* eslint-disable no-unreachable */
import { useMutation } from "@apollo/client";
import { Form, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { UPDATE_LEAD_OPPORTUNITY } from "../../graphql/mutation";
import useOpportunityUserTypes from "../../hooks/useOpportunityUserTypes";
import { checkAccessPermission, closeDialog } from "../../library/helpers";
import CustomEmpty from "../commonComponents/CustomEmpty";
import CustomSkeleton from "../commonComponents/CustomSkeleton";
import DialogFooter from "../commonComponents/dialogFooter";
import UserSelector from "../commonComponents/userSelector";
import { AccessPermissionAction } from "../../library/constants";

export default function LeadOpportunityUsersForm(props) {
  const { opportunity, refetch, refetchLeads } = props;
  const [form] = Form.useForm();
  const { leadOpportunityUsers } = opportunity;
  const [userFormData, setuserFormData] = useState([]);

  // UserSelector fetches its own user list (and appends deactivated assignees),
  // so the form no longer pulls one of its own — the old list only fed the
  // plain <Select> in the fallback branch this replaces.
  const canAddEditOpportunity = checkAccessPermission(
    AccessPermissionAction.LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT
  );
  // Only the ACTIVE assignments count as "already assigned" — same rule the
  // information form applies.
  const activeLeadUsers = useMemo(
    () => (leadOpportunityUsers || []).filter(item => item?.isActive),
    [leadOpportunityUsers]
  );

  // Resolved against the ROOT of the opportunity type's parent chain, which is
  // where user types are configured. The old query here passed no
  // `opportunityTypeId` at all, so an opportunity with no users assigned yet
  // had nothing to render and the dialog came up empty.
  const { userTypes, loading: userTypesLoader } = useOpportunityUserTypes({
    opportunityTypeId: opportunity?.opportunityTypeId || opportunity?.opportunityType?.id,
    locationId: opportunity?.location?.id,
    skip: activeLeadUsers.length > 0,
  });

  // Existing assignments win; the configured types are the blank-slate case.
  const dataSource = activeLeadUsers.length > 0 ? activeLeadUsers : userTypes;
  useEffect(() => {
    const ownerObj = {};
    for (let index = 0; index < opportunity?.leadOpportunityUsers?.length; index++) {
      const element = opportunity.leadOpportunityUsers[index];
      ownerObj[`leadOpportunityUsers-${element.id}`] = +element.userId;
    }
    form.setFieldsValue({ ...ownerObj });
    setuserFormData(opportunity?.leadOpportunityUsers || []);
  }, [opportunity]);
  const [updateLeadOpportunity, { loading: updateOpportunityLoader }] =
    useMutation(UPDATE_LEAD_OPPORTUNITY);
  const handleSave = async () => {
    const res = await updateLeadOpportunity({
      variables: {
        leadOpportunityId: opportunity.id,
        leadOpportunityUsers: userFormData.map(item => ({
          id: item.id,
          userTypeId: item.userTypeId,
          userId: item.userId,
        })),
      },
    });
    if (res.data.updateLeadOpportunity.statusCode === 200) {
      message.success(res.data.updateLeadOpportunity.message);
      refetch();
      refetchLeads && refetchLeads();
      closeDialog("editLeadOpportunityUsers");
    } else {
      message.error(res.data.updateLeadOpportunity.message);
    }
  };
  return (
    <>
      <Form onFinish={handleSave} layout="vertical" form={form}>
        {/* Mirrors the "Opportunity Users" section of
            leadOpportunityInformationForm: existing ACTIVE assignments when
            there are any, otherwise a blank slot per configured user type. Both
            render through the same UserSelector — `isUserType` is what tells
            the two shapes apart. */}
        {userTypesLoader ? (
          // Shaped like the fields it replaces — a short label bar over a
          // full-width input bar, three times — so the dialog doesn't resize
          // when the real selectors arrive.
          [0, 1, 2].map(row => (
            <div key={row} className="mb-4">
              <CustomSkeleton
                variant="button"
                block={false}
                skeletonCount={1}
                skeletonWidth={96}
                skeletonHeight={14}
                skeletonMarginBottom="8px"
              />
              <CustomSkeleton
                variant="button"
                skeletonCount={1}
                skeletonWidth="100%"
                skeletonHeight={32}
                skeletonMarginBottom="0"
              />
            </div>
          ))
        ) : isEmpty(dataSource) ? (
          <div className="my-2">
            <CustomEmpty description="No opportunity users available" />
          </div>
        ) : (
          dataSource.map((item, index) => {
            const isUserType = !item.userType;
            const userTypeName = isUserType ? item.name : item.userType.name;
            // Existing rows key their form field by the leadOpportunityUser id,
            // matching what the effect above put into form.setFieldsValue.
            const formFieldName = isUserType ? undefined : `leadOpportunityUsers-${item.id}`;

            return (
              <UserSelector
                key={isUserType ? index : `user-${item.id}`}
                validation
                name={formFieldName}
                placeholder={`Select ${userTypeName}`}
                handleUsersSelection={value =>
                  setuserFormData(prev => {
                    const temp = structuredClone(prev);
                    temp[index] = {
                      ...temp[index],
                      userTypeId: isUserType ? +item.id : +item.userTypeId,
                      userId: value,
                      userType: userTypeName,
                    };
                    return temp;
                  })
                }
                allowClear
                multiple={false}
                showAll={false}
                disabled={!canAddEditOpportunity}
                item={isUserType ? item : { name: userTypeName, item }}
                extraQueryParams={{
                  isAllLocationUsers: true,
                }}
              />
            );
          })
        )}
      </Form>
      {/* Nothing to save while the types are still loading or when there are
          none: submitting then sends an empty `leadOpportunityUsers` and shows
          a success toast for a no-op. Also gated on the edit permission, which
          previously only disabled the selectors — not the Save button. */}
      <DialogFooter
        loading={updateOpportunityLoader}
        disabled={userTypesLoader || isEmpty(dataSource) || !canAddEditOpportunity}
        handleConfirm={() => form.submit()}
        handleCancel={() => closeDialog("editLeadOpportunityUsers")}
      />
    </>
  );
}
